import React, { useState, useRef, useLayoutEffect } from "react";

/**
 * A custom React hook that returns a ref object and the width and height of the component.
 * The width and height are updated whenever the component is resized.
 * @returns {[React.RefObject<HTMLDivElement>, number, number]} A tuple containing the ref object, the width and the height of the component.
 */
export function useComponentDimensions(): [React.RefObject<HTMLDivElement>, number, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const element = ref.current;

    if (element) {
      // Measure the dimensions immediately
      setDimensions({
        width: element.getBoundingClientRect().width,
        height: element.getBoundingClientRect().height,
      });

      const resizeObserver = new ResizeObserver((entries) => {
        const newWidth = entries[0].contentRect.width;
        const newHeight = entries[0].contentRect.height;
        if (newWidth !== dimensions.width || newHeight !== dimensions.height) {
          setDimensions({ width: newWidth, height: newHeight });
        }
      });

      resizeObserver.observe(element);

      // Cleanup function
      return () => {
        resizeObserver.unobserve(element);
      };
    }
  }, []); // Empty dependency array

  return [ref, dimensions.width, dimensions.height];
}
